// src/components/teacher/TeacherSchedule.js
import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Skeleton,
	Alert,
	Button,
} from "@mui/material";
import { Calendar as CalendarIcon, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import {
	getAcademicYearRange,
	scheduleOverlapsAcademicYear,
} from "../../utils/academicYear";

const DAYS_OF_WEEK = [
	{ value: 1, label: "Monday" },
	{ value: 2, label: "Tuesday" },
	{ value: 3, label: "Wednesday" },
	{ value: 4, label: "Thursday" },
	{ value: 5, label: "Friday" },
	{ value: 6, label: "Saturday" },
];

const PERIODS = Array.from({ length: 10 }, (_, i) => ({
	value: i + 1,
	label: `${i + 1}`,
}));

const TeacherSchedule = ({ supabase, user }) => {
	const [schedules, setSchedules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [grades, setGrades] = useState({});
	const navigate = useNavigate();
	const { currentAcademicYear, loading: academicYearLoading } =
		useAcademicYear();

	// Fetch grades data
	useEffect(() => {
		const fetchGrades = async () => {
			try {
				const { data, error } = await supabase.from("grades").select("*");

				if (error) throw error;

				// Convert array to object with id as key for easier lookup
				const gradesObj = {};
				data.forEach((grade) => {
					gradesObj[grade.id] = grade;
				});

				setGrades(gradesObj);
			} catch (error) {
				console.error("Error fetching grades:", error);
			}
		};

		fetchGrades();
	}, [supabase]);

	// Fetch current academic year schedules for the teacher
	useEffect(() => {
		const fetchSchedule = async () => {
			if (!user || academicYearLoading || !currentAcademicYear) {
				if (!user) {
					setLoading(false);
					setSchedules([]);
				}
				return;
			}

			setLoading(true);
			try {
				const { start, end } = getAcademicYearRange(currentAcademicYear);
				const ayStart = start.format("YYYY-MM-DD");
				const ayEnd = end.format("YYYY-MM-DD");

				const { data, error } = await supabase
					.from("teacher_schedules")
					.select("*")
					.eq("teacher_id", user.id)
					.eq("status", "active")
					.lte("start_date", ayEnd)
					.or(`end_date.is.null,end_date.gte.${ayStart}`)
					.order("grade_id")
					.order("day_of_week")
					.order("period");

				if (error) throw error;
				const yearSchedules = (data || []).filter((schedule) =>
					scheduleOverlapsAcademicYear(schedule, currentAcademicYear)
				);
				setSchedules(yearSchedules);
			} catch (error) {
				console.error("Error fetching teacher schedules:", error);
				setSchedules([]);
			} finally {
				setLoading(false);
			}
		};

		fetchSchedule();
	}, [supabase, user, currentAcademicYear, academicYearLoading]);

	const viewScheduleDetails = () => {
		navigate("/schedule"); // Navigate to schedule management page
	};

	// Function to get class number for a specific day and period
	const getClassNumber = (day, period) => {
		const schedule = schedules.find(
			(s) =>
				s.day_of_week === day && s.period === period && s.status === "active"
		);

		if (schedule) {
			// Get grade name from the grades object using grade_id
			const gradeName = grades[schedule.grade_id]?.name || "";

			// Remove the word "Grade " from the grade name (e.g., "Grade 1A" -> "1A")
			const gradeNumber = gradeName.replace(/^Grade\s+/i, "");

			return gradeNumber || schedule.grade_id.toString().substring(0, 1);
		}

		return "-";
	};

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<Skeleton variant="text" height={40} width="30%" sx={{ mb: 2 }} />
				<Skeleton variant="rectangular" height={200} />
			</Box>
		);
	}

	if (schedules.length === 0) {
		return (
			<Paper
				elevation={0}
				sx={{
					p: 2,
					mt: 3,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<Box sx={{ p: 2, textAlign: "center" }}>
					<Alert
						severity="info"
						icon={<AlertCircle size={20} />}
						sx={{ mb: 2, maxWidth: 500, mx: "auto" }}>
						You don't have any timetable entries yet.
					</Alert>
					<Button
						variant="contained"
						startIcon={<CalendarIcon size={16} />}
						sx={{
							backgroundColor: "#6366f1",
							"&:hover": {
								backgroundColor: "#4f46e5",
							},
						}}
						onClick={viewScheduleDetails}>
						Manage Timetable
					</Button>
				</Box>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				mt: 3,
				border: "1px solid #e5e7eb",
				borderRadius: "8px",
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					My Timetable
				</Typography>
				<Button
					variant="text"
					startIcon={<CalendarIcon size={16} />}
					onClick={viewScheduleDetails}
					sx={{
						color: "#6366f1",
						textTransform: "none",
						fontSize: "0.875rem",
						"&:hover": {
							backgroundColor: "rgba(99, 102, 241, 0.04)",
						},
					}}>
					Manage Timetable
				</Button>
			</Box>

			{/* Schedule Overview Table */}
			<Box sx={{ mt: 2, mb: 3 }}>
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell
									rowSpan={2}
									sx={{
										borderRight: "1px solid rgba(224, 224, 224, 1)",
										fontWeight: "bold",
										verticalAlign: "middle",
									}}>
									Day
								</TableCell>
								<TableCell
									align="center"
									colSpan={PERIODS.length}
									sx={{
										borderBottom: "1px solid rgba(224, 224, 224, 1)",
										fontWeight: "bold",
									}}>
									Periods
								</TableCell>
							</TableRow>
							<TableRow>
								{PERIODS.map((period) => (
									<TableCell
										key={period.value}
										align="center"
										sx={{
											px: 1,
											fontWeight: "bold",
										}}>
										{period.value}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{DAYS_OF_WEEK.map((day) => (
								<TableRow key={day.value}>
									<TableCell
										sx={{
											borderRight: "1px solid rgba(224, 224, 224, 1)",
											fontWeight: "bold",
										}}>
										{day.label}
									</TableCell>
									{PERIODS.map((period) => {
										const classNum = getClassNumber(day.value, period.value);
										const hasClass = classNum !== "-";
										return (
											<TableCell
												key={period.value}
												align="center"
												sx={{
													backgroundColor: hasClass ? "#dcfce7" : "inherit",
													color: hasClass ? "#059669" : "inherit",
													px: 1,
												}}>
												{classNum}
											</TableCell>
										);
									})}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Paper>
	);
};

export default TeacherSchedule;
